import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { tg } from "../telegram";
import { fetchExport, saveBlob } from "../download";
import { shareOrSave } from "../share";

export type ShareFormat = "pdf" | "docx";

/** Describes what a Share bottom-sheet should offer. `key` is a stable identity
 *  (clause id / "doc" / "file-<id>") so the blob pre-fetch doesn't re-run on every
 *  parent render. */
export interface ShareSpec {
  key: string;
  title: string;
  withFormat: boolean; // true → PDF/Word toggle (documents); false → native (files)
  url: (format: ShareFormat) => string;
  fallbackName: (format: ShareFormat) => string;
  mime: (format: ShareFormat) => string;
}

interface BlobState {
  loading: boolean;
  blob?: Blob;
  filename?: string;
  mime?: string;
  error?: string;
}

export default function ShareSheet({ spec, onClose }: { spec: ShareSpec; onClose: () => void }) {
  const { t } = useT();
  const [format, setFormat] = useState<ShareFormat>("pdf");
  const [st, setSt] = useState<BlobState>({ loading: true });
  const [acting, setActing] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // Pre-fetch the file for the current format so the Share tap stays inside the
  // user gesture (required by iOS/Safari) and Download is instant.
  useEffect(() => {
    let alive = true;
    setSt({ loading: true });
    setNote(null);
    fetchExport(spec.url(format), spec.fallbackName(format))
      .then(({ blob, filename }) => {
        if (alive) setSt({ loading: false, blob, filename, mime: spec.mime(format) });
      })
      .catch((e: Error) => {
        if (alive) setSt({ loading: false, error: e.message });
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec.key, format]);

  const ready = !st.loading && !st.error && st.blob && st.filename;

  const onShare = async () => {
    if (!ready || acting) return;
    tg()?.HapticFeedback?.impactOccurred("light");
    setActing(true);
    try {
      const r = await shareOrSave(st.blob!, st.filename!, st.mime || "", spec.title);
      if (r === "downloaded") {
        setNote(t("share.downloaded"));
        setActing(false);
        return;
      }
      onClose();
    } catch {
      setNote(t("share.fail"));
      setActing(false);
    }
  };

  const onDownload = () => {
    if (!ready) return;
    tg()?.HapticFeedback?.impactOccurred("light");
    saveBlob(st.blob!, st.filename!);
    onClose();
  };

  return (
    <div className="modal-overlay share-overlay" onClick={onClose}>
      <div className="share-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="share-grabber" />
        <div className="share-title">{spec.title}</div>

        {spec.withFormat && (
          <div className="share-formats" role="radiogroup" aria-label={t("share.format")}>
            {(["pdf", "docx"] as const).map((f) => (
              <button
                key={f}
                type="button"
                className={"share-format" + (format === f ? " active" : "")}
                onClick={() => setFormat(f)}
                role="radio"
                aria-checked={format === f}
              >
                <span className="share-format-ico">{f === "pdf" ? "📕" : "📄"}</span>
                {f === "pdf" ? t("fmt.pdf") : t("fmt.word")}
              </button>
            ))}
          </div>
        )}

        {st.error && <div className="alert error">{st.error}</div>}
        {note && <div className="share-note">{note}</div>}

        <button className="btn share-primary" onClick={onShare} disabled={!ready || acting}>
          {st.loading ? <span className="spinner tiny" /> : <span aria-hidden="true">🔗</span>}{" "}
          {t("share.button")}
        </button>
        <div className="share-secondary-row">
          <button className="btn secondary" onClick={onDownload} disabled={!ready}>
            ⬇ {t("share.download")}
          </button>
          <button className="btn ghost" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
